import { initContract } from "@ts-rest/core"
import type {
  createLmsIntegration,
  createLmsPackage,
  createOrgLmsProbePackage,
  deleteLmsLearnerLink,
  getLmsOverview,
  importLmsLearnerLinks,
  linkLmsLearner,
  regenerateLmsIntegration,
  updateLmsIntegration,
} from "./lib/lms"
import { z } from "zod"
import type { FrameworkGenerationInput } from "./lib/feedback-frameworks/customer-material"
import {
  RECORDING_SCOPES,
  aclPriorityMax,
  aclPriorityMin,
} from "@simulead/shared"
import type {
  getAuthedUsers,
  getSession,
  makeRoomForFloatingLicenseOrgs,
} from "./lib/auth"
import {
  getUsers,
  type createUpdateUser,
  type getAllUsersAcrossOrgs,
  type updateUser,
  type getUsersBasicForOrg,
  type getCurrentUserPermissions,
  type archiveUser,
} from "./lib/user"
import {
  createOrg,
  type updateOrg,
  type getOrgStats,
  type getOrgMostPopularStats,
} from "./lib/org"
import {
  type createCall,
  type deleteCall,
  type getCall,
  type getCallDatesForUser,
  type getCallsForUser,
  type getCallsForUserBasic,
  type getMemoriesForUser,
  type processOrphanedCalls,
  type restartCall,
  type setDemoCallEmail,
  type updateCall,
} from "./lib/call"
import type { updateUserRoles } from "./lib/roles"
import type { runFeedbackTestBench } from "./lib/feedback-frameworks/test-bench"
import type {
  approveScenarioSubmission,
  declineScenarioSubmission,
  getScenarioReviewQueue,
} from "./lib/admin-review"
import type { explainScenarioVisibility } from "./lib/scenario-visibility"
import type {
  listContent,
  getContentRecord,
  getContentVersions,
  saveContentRecord,
  deleteContentRecord,
  duplicateContentRecord,
  restoreContentVersion,
  uploadContentAsset,
  exportContent,
  exportContentCsv,
  importContent,
  getContentUsage,
  getCoreCatalogue,
  setCoreFlag,
} from "./lib/content-editor"
import type {
  verifyContentMigration,
  verifyAccessForUser,
  getVerificationSubjects,
} from "./lib/migration-verify"
import { getVoiceOptions } from "./lib/vapi"
import type {
  getAccessControlData,
  getAccessControlSubjectsForOrg,
  previewAccessForUser,
  searchScenariosForAcl,
  saveAccessControlEntry,
  deleteAccessControlEntry,
  getAccessForContentItem,
} from "./lib/access-control"
import type { getMcpAuthorizationRequest } from "./lib/mcp/oauth"
import type {
  getFrameworkLibrary,
  deleteFeedbackFrameworkChecked,
  generateFrameworkDraft,
  getRecentCallsForBench,
  previewFeedbackFramework,
  publishFeedbackFramework,
  setFrameworkMapping,
  FrameworkMappingTarget,
} from "./lib/feedback-frameworks/admin"
import type {
  createFeedbackFramework,
  getAllFeedbackFrameworks,
  updateFeedbackFramework,
} from "./lib/cms"
import type { FeedbackFramework } from "./lib/feedback-frameworks/index"
import type {
  createEarlyAccessFeature,
  deleteEarlyAccessFeature,
  duplicateEarlyAccessFeature,
  EarlyAccessFeatureFields,
  getAdminEarlyAccessFeatures,
  getEarlyAccessFeaturesForUser,
  updateEarlyAccessFeature,
} from "./lib/early-access"
import type {
  updateUserPermissions,
  setOrgPermissionForAllUsers,
} from "./lib/permissions"
import type { getFrontendCmsData } from "./lib/cms"
import type { trackEvent } from "./lib/log"
import type {
  createPresentation,
  generatePresentationCribSheet,
  getPresentation,
  getPresentationsForUser,
  extractPresentationSlides,
} from "./lib/presentation"
import type {
  renameStudioCharacterInContent,
  generateScenario,
  regenerateScenarioPartial,
  submitToWebhook,
  OrgScenarioFormData,
  GeneratedContent,
  SubmissionData,
} from "./lib/org-scenario-creator"
import type { getTranscription } from "./lib/transcription"
import type {
  getCallRecordingPlaybackUrls,
  getPresentationRecordingPlaybackUrls,
  getRecordingUploadSignedUrl,
  sweepOrphanedRecordings,
} from "./lib/recording"
import type {
  createCallShare,
  deleteCallSharesForCall,
  deleteCallShare,
} from "./lib/call-share"
import type { syncAllUsersWithIntercom } from "./lib/intercom"
import type {
  createStreakConfiguration,
  updateStreakConfiguration,
  deleteStreakConfiguration,
} from "./lib/streak-configuration"
import type { getUsersStreaks, sendStreakNotifications } from "./lib/streaks"
import type { sendInactivityNotifications } from "./lib/inactivity-notifications"
import type {
  createScenarioCreatorCall,
  getScenarioCreatorCall,
  updateScenarioCreatorCall,
} from "./lib/scenario-creator-call"
import type { deleteScenario } from "./lib/scenarios"
import type { sendMessageToUsers } from "./lib/slack"
import type {
  generateInsightForOrg,
  generateInsightsForAllOrgs,
  getInsightsForOrg,
  getOrGenerateInsightForFilters,
} from "./lib/insights"
import type {
  getJourneyCompletionReport,
  getSimulationCompletionReport,
  sendCompletionNudges,
} from "./lib/reports"
import type { getAnalyticsOutput, AnalyticsOutputName } from "./lib/analytics"
import type { createReminder, sendReminders } from "./lib/reminders"
import type {
  getTeams,
  getTeam,
  createTeam,
  deleteTeam,
  updateTeam,
  addUsersToTeam,
  removeUsersFromTeam,
} from "./lib/team"
import type {
  backfillVapiRecordingUrls,
  executeRetentionPolicy,
} from "./lib/scripts"
import type { recordUserCountSnapshotsForAllOrgs } from "./lib/user-count-snapshot"
import type { getScenarioStudioForOrg } from "./lib/scenario-submissions"
import type {
  createStudioDraft,
  createStudioEditDraft,
  deleteStudioDraft,
  getStudioCategoriesForOrg,
  getStudioDraftForOrg,
  getStudioReferenceScenariosForOrg,
  submitStudioDraft,
  updateStudioDraft,
} from "./lib/scenario-studio"
import type {
  createDemoScenario,
  createPersonalScenario,
  getPersonalScenarioForUser,
  updatePersonalScenario,
} from "./lib/personal-scenario"
import type { generateStudioCharacterPrompt } from "./lib/scenario-pipeline"
import type { getCharacters } from "./lib/cms"

import { KeyName, Permission, PeriodUnit, Prisma } from "@simulead/database"
import type {
  getAdminPortalLink,
  getAuthorizationLink,
  createWorkosProfileSession,
  deleteWorkosProfileSession,
} from "./lib/workos"
import type {
  generateAllTranslations,
  reportTranslationContentChange,
  getTranslationsForScenario,
  getScenarioTranslationSummary,
  startScenarioTranslation,
} from "./lib/translation"
import type { getLiveApiEphemeralToken } from "./lib/live-api"
import type { getEgressInfo, getNetworkCheckResult } from "./lib/diagnostics"
import type {
  generateFeedbackPodcast,
  generatePresentationFeedbackPodcast,
} from "./lib/tts"
import type {
  generateMediaCoverage,
  suggestKeyMessages,
} from "./lib/media-coverage"
import type { generateNewspaperImages } from "./lib/media-newspaper-images"
import type {
  chooseMultiSimSpeaker,
  analyzeMultiSimTranscript,
} from "./lib/multi-sim"
import type {
  getJourneys,
  getJourney,
  createJourney,
  updateJourney,
  archiveJourney,
  restoreJourney,
  getJourneyAssignments,
  createJourneyAssignment,
  bulkUpdateJourneyAssignmentDueDates,
  deleteJourneyAssignment,
  getUserJourneys,
  getJourneyProgress,
  generateJourneyContent,
} from "./lib/journeys"
import type {
  sendJourneyManualNudges,
  processJourneyReminders,
  getJourneyNotificationConfig,
  upsertJourneyNotificationConfig,
} from "./lib/journey-notifications"

// ============================================================================
// Journey Types (for Learning Paths feature)
// ============================================================================

// Scenario within a journey (includes position for ordering)
export const JourneyScenarioSchema = z.object({
  id: z.string(),
  journeyId: z.string(),
  scenarioId: z.string(),
  position: z.number(),
  // Difficulty required to complete this step: null = any difficulty,
  // "internal.standard" (JOURNEY_STEP_STANDARD_DIFFICULTY) = the base/Standard
  // level only, otherwise a CMS difficulty levelName
  difficultyLevel: z.string().nullable(),
  createdAt: z.string(),
})
export type JourneyScenario = z.infer<typeof JourneyScenarioSchema>

// User's progress on a specific scenario within a journey
export const JourneyScenarioProgressSchema = z.object({
  // The JourneyScenario row this progress belongs to — scenarioId alone is
  // not unique once the same scenario appears at multiple difficulties
  journeyScenarioId: z.string(),
  scenarioId: z.string(),
  // Difficulty required for this step (null = any,
  // "internal.standard" = Standard, else a CMS levelName)
  difficultyLevel: z.string().nullable(),
  completed: z.boolean(),
  completedAt: z.string().nullable(),
  // The call ID that completed this scenario (for linking to feedback)
  completingCallId: z.string().nullable(),
  // Eligible attempts (>=60s, not restarted) the user has made on this step
  attemptCount: z.number(),
  // Whether this scenario is unlocked based on sequential completion
  // (completion can come from attempts when the journey allows it)
  isUnlocked: z.boolean(),
})
export type JourneyScenarioProgress = z.infer<
  typeof JourneyScenarioProgressSchema
>

// Journey assignment to a user
export const JourneyAssignmentSchema = z.object({
  id: z.string(),
  journeyId: z.string(),
  userId: z.number().nullable(),
  teamId: z.string().nullable(),
  dueDate: z.string().nullable(),
  assignedAt: z.string(),
})
export type JourneyAssignment = z.infer<typeof JourneyAssignmentSchema>

// Full journey object
export const JourneySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  orgId: z.number(),
  // When true, attempting a step scenarioAttemptsRequired times counts as
  // completing it (default false = a successful call is required)
  acceptScenarioAttemptsAsCompletion: z.boolean(),
  // Attempts that complete a step; only consulted when the toggle above is on
  scenarioAttemptsRequired: z.number(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  scenarios: z.array(JourneyScenarioSchema),
})
export type Journey = z.infer<typeof JourneySchema>

// Journey with user's progress (for user-facing views)
export const JourneyWithProgressSchema = JourneySchema.extend({
  assignment: JourneyAssignmentSchema,
  progress: z.object({
    completedCount: z.number(),
    totalCount: z.number(),
    scenarioProgress: z.array(JourneyScenarioProgressSchema),
    isCompleted: z.boolean(),
  }),
})
export type JourneyWithProgress = z.infer<typeof JourneyWithProgressSchema>

// Journey with assignment counts (for admin views)
export const JourneyWithStatsSchema = JourneySchema.extend({
  assignmentCount: z.number(),
  completedCount: z.number(),
})
export type JourneyWithStats = z.infer<typeof JourneyWithStatsSchema>

// @todo
// Add summaries for OpenAPI
// Add Authorization header only where needed
// Add correct response codes for errors
// Use Zod to form request and response bodies (zod-prisma)

export const baseHeaders = z.object({
  "content-type": z.optional(z.string()),
  "token-type": z
    .enum([
      "firebase",
      "workos-profile-session",
      "testing",
      "login-token",
      // ENG-1471: the scoped LMS launch session (see lib/lms-router.ts)
      "lms-session",
    ])
    .optional(),
  token: z.string().optional(),
  "user-id": z.string().optional(),
  "emulate-user-id": z.string().optional(),
  "scenario-creator-call-token": z.string().optional(),
  authorization: z.string().optional(),
})

export type BaseHeaders = z.infer<typeof baseHeaders>

const c = initContract()

// Typed client/access failures on the Personal Scenario Creator endpoints (see
// PersonalScenarioError): 400 malformed or incomplete input, 403 no PSC
// access, 404 a scenario that is missing or not the caller's — the two are
// indistinguishable by design so ownership checks never reveal existence.
const personalScenarioErrorResponses = {
  400: c.type<{ message: string }>(),
  403: c.type<{ message: string }>(),
  404: c.type<{ message: string }>(),
}

// ENG-1138: the content editor's 4xx replies (bad input, missing record, a save
// that lost a race or a delete that would orphan history), each with a message
// the editor shows as-is. See ContentEditorError.
const contentErrorResponses = {
  400: c.type<{ message: string }>(),
  404: c.type<{ message: string }>(),
  409: c.type<{ message: string }>(),
}

export const contractRouter = c.router(
  {
    index: {
      method: "GET",
      path: "/",
      responses: {
        200: z.object({
          message: z.string(),
        }),
      },
    },
    health: {
      method: "GET",
      path: "/health",
      responses: {
        200: z.object({
          status: z.string(),
        }),
      },
    },
    // Public endpoint for the frontend to check if an outage banner should be shown
    getOutageBanner: {
      method: "GET",
      path: "/outage-banner",
      responses: {
        200: z.object({
          enabled: z.boolean(),
          message: z.string(),
        }),
      },
    },
    syncUsersWithIntercom: {
      method: "POST",
      path: "/scripts/sync-users-with-intercom",
      responses: {
        200: c.type<Awaited<ReturnType<typeof syncAllUsersWithIntercom>>>(),
      },
      body: c.noBody(),
    },
    getAuthData: {
      method: "GET",
      path: "/auth/data",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getAuthedUsers>>>(),
      },
    },
    getCmsData: {
      method: "GET",
      path: "/cms-data",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getFrontendCmsData>>>(),
      },
    },
    getUsers: {
      method: "GET",
      path: "/orgs/:orgId/users",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getUsers>>>(),
        400: c.type<{ error: string }>(),
      },
      query: c.type<
        Omit<Parameters<typeof getUsers>[0], "orgId"> | undefined
      >(),
    },
    getAllUsers: {
      method: "GET",
      path: "/users",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getAllUsersAcrossOrgs>>>(),
      },
    },
    createUser: {
      method: "POST",
      path: "/orgs/:orgId/users",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createUpdateUser>>>(),
      },
      // Body derives from createUpdateUser's params, omitting only the fields the
      // route derives itself. Everything else — including the optional
      // `reactivateArchived` flag (CSV import sends `false` to avoid silently
      // unarchiving rotated-out floating-license users) and `sendWelcomeEmail` —
      // is intentionally part of the createUser body.
      body: c.type<
        Omit<
          Parameters<typeof createUpdateUser>[0],
          "userId" | "orgId" | "createdByUser"
        >
      >(),
    },
    createOrg: {
      method: "POST",
      path: "/orgs",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createOrg>>>(),
      },
      body: c.type<Parameters<typeof createOrg>[0]>(),
    },
    getOrgStats: {
      method: "GET",
      path: "/orgs/:orgId/stats",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getOrgStats>>>(),
      },
      query: c.type<Omit<Parameters<typeof getOrgStats>[0], "orgId">>(),
    },
    getOrgMostPopularStats: {
      method: "GET",
      path: "/orgs/:orgId/stats/most-popular",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getOrgMostPopularStats>>>(),
      },
    },
    getSimulationCompletionReport: {
      method: "GET",
      path: "/orgs/:orgId/reports/simulation-completion",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof getSimulationCompletionReport>>
        >(),
      },
      query: c.type<
        | Omit<Parameters<typeof getSimulationCompletionReport>[0], "orgId">
        | undefined
      >(),
    },
    // Admin: email a completion reminder (via SendGrid) to selected users.
    // Each recipient gets a personalised list of their incomplete scenarios
    // (first 5, then "...and more"); content is composed server-side.
    // scenarioIds narrows to the filtered view — omit for all org scenarios.
    sendCompletionNudge: {
      method: "POST",
      path: "/orgs/:orgId/reports/simulation-completion/nudge",
      responses: {
        200: c.type<Awaited<ReturnType<typeof sendCompletionNudges>>>(),
        400: c.type<{ message: string }>(),
      },
      body: c.type<{
        userIds: number[]
        scenarioIds?: string[]
        categoryId?: string
        // Optional date range so reminder emails only consider completions
        // within the active reporting window (matching the completion table).
        range?: { from: string; to: string }
        // Dry-run: return the rendered email + who'd be sent/skipped without
        // sending. Powers the confirmation-modal preview.
        preview?: boolean
      }>(),
    },
    getJourneyCompletionReport: {
      method: "GET",
      path: "/orgs/:orgId/reports/journey-completion",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getJourneyCompletionReport>>>(),
      },
      query: c.type<
        | Omit<Parameters<typeof getJourneyCompletionReport>[0], "orgId">
        | undefined
      >(),
    },
    // Ambr Analytics Engine: single endpoint that resolves any registry entry
    // by name. Frontend AnalyticsVisual narrows the response on `type`.
    getAnalyticsOutput: {
      method: "GET",
      path: "/orgs/:orgId/analytics",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getAnalyticsOutput>>>(),
        400: c.type<{ message: string }>(),
      },
      query: c.type<{
        name: AnalyticsOutputName
        // ts-rest serialises the filters object via JSON in the query string
        filters?: string
      }>(),
    },
    getCall: {
      method: "GET",
      path: "/calls/:callId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getCall>>>(),
        404: c.type<{ message: string }>(),
      },
    },
    deleteCall: {
      method: "DELETE",
      path: "/calls/:callId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteCall>>>(),
      },
      body: c.noBody(),
    },
    getCallsForUser: {
      method: "GET",
      path: "/users/:userId/calls",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getCallsForUser>>>(),
      },
      // viewerUserId is the session user, never a client-supplied value.
      query: c.type<
        Omit<Parameters<typeof getCallsForUser>[0], "userId" | "viewerUserId">
      >(),
    },
    getCallsForUserBasic: {
      method: "GET",
      path: "/users/:userId/calls-basic",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getCallsForUserBasic>>>(),
      },
      query: c.type<
        Omit<
          Parameters<typeof getCallsForUserBasic>[0],
          "userId" | "viewerUserId"
        >
      >(),
    },
    getCallDatesForUser: {
      method: "GET",
      path: "/users/:userId/call-dates",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getCallDatesForUser>>>(),
      },
    },
    getMemoriesForUser: {
      method: "GET",
      path: "/users/:userId/memories",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getMemoriesForUser>>>(),
      },
    },
    createCallForUser: {
      method: "POST",
      path: "/users/:userId/calls",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createCall>>>(),
      },
      body: c.type<Omit<Parameters<typeof createCall>[0], "userId">>(),
    },
    createCall: {
      method: "POST",
      path: "/calls",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createCall>>>(),
        400: c.type<{ message: string }>(),
      },
      body: c.type<Omit<Parameters<typeof createCall>[0], "userId">>(),
    },
    updateCall: {
      method: "PUT",
      path: "/calls/:callId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateCall>>>(),
      },
      body: c.type<Omit<Parameters<typeof updateCall>[0], "callId">>(),
    },
    // Attaches a demo visitor's email to an anonymous call after it ended
    // (feedback-gated demo lead capture). Claim-once; see setDemoCallEmail.
    setDemoCallEmail: {
      method: "POST",
      path: "/calls/:callId/demo-email",
      responses: {
        200: c.type<Awaited<ReturnType<typeof setDemoCallEmail>>>(),
        400: c.type<{ message: string }>(),
      },
      body: c.type<{ email: string }>(),
    },
    // Marks a call as restarted with server-side timestamp
    restartCall: {
      method: "POST",
      path: "/calls/:callId/restart",
      responses: {
        200: c.type<Awaited<ReturnType<typeof restartCall>>>(),
      },
      body: c.type<Record<string, never>>(),
    },
    updateRolesForUser: {
      method: "POST",
      path: "/orgs/:orgId/users/:userId/roles",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateUserRoles>>>(),
      },
      body: c.type<
        Omit<Parameters<typeof updateUserRoles>[0], "userId" | "orgId">
      >(),
    },
    updateUser: {
      method: "PUT",
      path: "/orgs/:orgId/users/:userId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateUser>>>(),
        400: c.type<{ error: string }>(),
      },
      body: c.type<
        Omit<Parameters<typeof updateUser>[0], "userId" | "orgId">
      >(),
    },
    updateUserPermissions: {
      method: "PUT",
      path: "/users/:userId/permissions",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateUserPermissions>>>(),
      },
      body: c.type<
        Omit<Parameters<typeof updateUserPermissions>[0], "userId">
      >(),
    },
    setOrgPermissionForAllUsers: {
      method: "PUT",
      path: "/orgs/:orgId/permissions",
      responses: {
        200: c.type<Awaited<ReturnType<typeof setOrgPermissionForAllUsers>>>(),
      },
      body: c.type<
        Omit<Parameters<typeof setOrgPermissionForAllUsers>[0], "orgId">
      >(),
    },
    updateOrg: {
      method: "PUT",
      path: "/orgs/:orgId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateOrg>>>(),
      },
      body: c.type<Omit<Parameters<typeof updateOrg>[0], "orgId">>(),
    },
    // ENG-1474 / ENG-1478: customer-admin LMS (SCORM) integration management.
    // All require an org admin of an org with LMS delivery switched on.
    getLmsOverview: {
      method: "GET",
      path: "/orgs/:orgId/lms",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getLmsOverview>>>(),
      },
    },
    createLmsIntegration: {
      method: "POST",
      path: "/orgs/:orgId/lms/integrations",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createLmsIntegration>>>(),
      },
      body: z.object({ name: z.string().min(1).max(100) }),
    },
    updateLmsIntegration: {
      method: "PATCH",
      path: "/orgs/:orgId/lms/integrations/:integrationId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateLmsIntegration>>>(),
      },
      body: z.object({
        name: z.string().min(1).max(100).optional(),
        enabled: z.boolean().optional(),
      }),
    },
    regenerateLmsIntegration: {
      method: "POST",
      path: "/orgs/:orgId/lms/integrations/:integrationId/regenerate",
      responses: {
        200: c.type<Awaited<ReturnType<typeof regenerateLmsIntegration>>>(),
      },
      body: c.noBody(),
    },
    // ENG-1472: the LMS probe stamped with the org (Admin > Settings)
    createLmsProbePackage: {
      method: "POST",
      path: "/orgs/:orgId/lms/probe-package",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createOrgLmsProbePackage>>>(),
      },
      body: c.noBody(),
    },
    createLmsPackage: {
      method: "POST",
      path: "/orgs/:orgId/lms/integrations/:integrationId/packages",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createLmsPackage>>>(),
      },
      body: z.object({
        scenarioId: z.string().min(1),
        difficultyLevel: z.string().max(100).nullable().optional(),
      }),
    },
    importLmsLearnerLinks: {
      method: "POST",
      path: "/orgs/:orgId/lms/integrations/:integrationId/links/import",
      responses: {
        200: c.type<Awaited<ReturnType<typeof importLmsLearnerLinks>>>(),
      },
      body: z.object({
        rows: z
          .array(
            z.object({
              learnerId: z.string().max(256),
              email: z.string().max(254),
            }),
          )
          .max(5000),
      }),
    },
    linkLmsLearner: {
      method: "POST",
      path: "/orgs/:orgId/lms/integrations/:integrationId/links",
      responses: {
        200: c.type<Awaited<ReturnType<typeof linkLmsLearner>>>(),
      },
      body: z.object({
        learnerId: z.string().min(1).max(256),
        userId: z.number().int(),
      }),
    },
    deleteLmsLearnerLink: {
      method: "DELETE",
      path: "/orgs/:orgId/lms/links/:linkId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteLmsLearnerLink>>>(),
      },
      body: c.noBody(),
    },
    trackEvent: {
      method: "POST",
      path: "/track",
      responses: {
        200: c.noBody(),
      },
      body: c.type<Parameters<typeof trackEvent>[0]>(),
    },
    getPresentation: {
      method: "GET",
      path: "/presentations/:id",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getPresentation>>>(),
      },
    },
    createPresentation: {
      method: "POST",
      path: "/users/:userId/presentations",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createPresentation>>>(),
        403: c.type<{ message: string }>(),
      },
      body: c.type<Omit<Parameters<typeof createPresentation>[0], "userId">>(),
    },
    generatePresentationCribSheet: {
      method: "POST",
      path: "/presentations/:id/crib-sheet",
      responses: {
        200: c.noBody(),
      },
      body: c.noBody(),
    },
    getPresentationsForUser: {
      method: "GET",
      path: "/users/:userId/presentations",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getPresentationsForUser>>>(),
      },
      query:
        c.type<Omit<Parameters<typeof getPresentationsForUser>[0], "userId">>(),
    },
    deletePresentation: {
      method: "DELETE",
      path: "/presentations/:id",
      responses: {
        200: c.type<{ presentation: any }>(),
      },
      body: c.noBody(),
    },
    // TTS Presentation Podcast endpoint - generates audio from presentation feedback
    generatePresentationPodcast: {
      method: "POST",
      path: "/presentations/:presentationId/tts-podcast",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof generatePresentationFeedbackPodcast>>
        >(),
        400: c.type<{ message: string }>(),
        404: c.type<{ message: string }>(),
        500: c.type<{ message: string }>(),
      },
      body: c.noBody(),
    },
    getTranscription: {
      method: "POST",
      path: "/transcription",
      contentType: "multipart/form-data",
      body: c.type<{ audio: File }>(),
      query: c.type<Pick<Parameters<typeof getTranscription>[0], "provider">>(),
      responses: {
        200: c.type<Awaited<ReturnType<typeof getTranscription>>>(),
      },
    },
    // Accepts a slide deck PDF for presentation practice and returns
    // Gemini-extracted per-slide content to ground Q&A and analysis. The PDF
    // is read in memory only — it is never stored server-side.
    uploadPresentationSlides: {
      method: "POST",
      path: "/presentation-slides",
      contentType: "multipart/form-data",
      body: c.type<{ slides: File }>(),
      responses: {
        200: c.type<Awaited<ReturnType<typeof extractPresentationSlides>>>(),
        403: c.type<{ message: string }>(),
      },
    },
    getCallRecordingUrl: {
      method: "GET",
      path: "/calls/:callId/recording-url",
      responses: {
        200: c.type<
          NonNullable<Awaited<ReturnType<typeof getCallRecordingPlaybackUrls>>>
        >(),
        404: c.type<{ message: string }>(),
      },
    },
    getPresentationRecordingUrl: {
      method: "GET",
      path: "/presentations/:presentationId/recording-url",
      responses: {
        200: c.type<
          NonNullable<
            Awaited<ReturnType<typeof getPresentationRecordingPlaybackUrls>>
          >
        >(),
        404: c.type<{ message: string }>(),
      },
    },
    getRecordingUploadUrl: {
      method: "POST",
      path: "/recording-upload-url",
      // Runtime-validated Zod body (not `c.type`, which is compile-time only):
      // this endpoint mints a write URL, so a malformed body — e.g. a
      // `sessionId` sent as an object — must be rejected with a 400 rather
      // than reaching the key builder (where it would throw a 500 on
      // `.replace`). `ownerId` is injected server-side from the authed
      // session (never sent by the client) for the user-scoped
      // presentation/qanda key, so it's deliberately absent here.
      body: z.object({
        scope: z.enum(RECORDING_SCOPES),
        sessionId: z.string(),
        fileName: z.string(),
        contentType: z.string(),
      }),
      responses: {
        200: c.type<Awaited<ReturnType<typeof getRecordingUploadSignedUrl>>>(),
        404: c.type<{ message: string }>(),
      },
    },
    createCallShare: {
      method: "POST",
      path: "/call-share",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createCallShare>>>(),
      },
      body: c.type<Omit<Parameters<typeof createCallShare>[0], "byUser">>(),
    },
    deleteCallSharesForCall: {
      method: "DELETE",
      path: "/calls/:callId/call-shares",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteCallSharesForCall>>>(),
      },
      body: c.noBody(),
    },
    deleteCallShare: {
      method: "DELETE",
      path: "/call-shares/:callShareId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteCallShare>>>(),
      },
      body: c.noBody(),
    },
    getUsersBasic: {
      method: "GET",
      path: "/orgs/:orgId/users-basic",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getUsersBasicForOrg>>>(),
      },
    },
    createStreakConfiguration: {
      method: "POST",
      path: "/orgs/:orgId/streak-configurations",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createStreakConfiguration>>>(),
      },
      body: c.type<Omit<Prisma.StreakConfigurationCreateInput, "org">>(),
    },
    updateStreakConfiguration: {
      method: "PUT",
      path: "/streak-configurations/:id",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateStreakConfiguration>>>(),
      },
      body: c.type<{
        data: Prisma.StreakConfigurationUpdateInput
      }>(),
    },
    deleteStreakConfiguration: {
      method: "DELETE",
      path: "/streak-configurations/:id",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteStreakConfiguration>>>(),
      },
      body: c.noBody(),
    },
    sendStreakNotifications: {
      method: "GET",
      path: "/scripts/send-streak-notifications",
      responses: {
        200: c.type<Awaited<ReturnType<typeof sendStreakNotifications>>>(),
      },
      query: c.type<{
        sendNotifications?: string
      }>(),
    },
    sendInactivityNotifications: {
      method: "GET",
      path: "/scripts/send-inactivity-notifications",
      responses: {
        200: c.type<Awaited<ReturnType<typeof sendInactivityNotifications>>>(),
      },
      query: c.type<{
        sendNotifications?: string
      }>(),
    },
    getWorkosAdminPortalLink: {
      method: "GET",
      path: "/orgs/:orgId/workos/admin-portal-link",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getAdminPortalLink>>>(),
      },
      query:
        c.type<
          Omit<
            Parameters<typeof getAdminPortalLink>[0],
            "orgId" | "requestingUserEmail"
          >
        >(),
    },
    getWorkosAuthorizationLink: {
      method: "GET",
      path: "/workos/authorization-link",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getAuthorizationLink>>>(),
      },
      query: c.type<Parameters<typeof getAuthorizationLink>[0]>(),
    },
    createWorkosProfileSession: {
      method: "POST",
      path: "/workos/profile-sessions",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createWorkosProfileSession>>>(),
      },
      body: c.type<Parameters<typeof createWorkosProfileSession>[0]>(),
    },
    // Deletes the WorkOS profile session to invalidate the JWT on logout
    // Token is read from the authorization header, not the body
    deleteWorkosProfileSession: {
      method: "DELETE",
      path: "/workos/profile-sessions",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteWorkosProfileSession>>>(),
      },
      body: c.noBody(),
    },
    syncWithWorkos: {
      method: "POST",
      path: "/orgs/:orgId/sync-with-workos",
      responses: {
        200: c.noBody(),
      },
      body: c.noBody(),
    },
    getUsersStreaks: {
      method: "GET",
      path: "/users/streaks",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getUsersStreaks>>>(),
      },
      query: c.type<Parameters<typeof getUsersStreaks>[0]>(),
    },
    getScenarioCreatorCall: {
      method: "GET",
      path: "/scenario-creator-calls/:id",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getScenarioCreatorCall>>>(),
      },
      query: c.type<Omit<Parameters<typeof getScenarioCreatorCall>[0], "id">>(),
    },
    createScenarioCreatorCall: {
      method: "POST",
      path: "/scenario-creator-calls",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createScenarioCreatorCall>>>(),
      },
      body: c.type<Parameters<typeof createScenarioCreatorCall>[0]>(),
    },
    updateScenarioCreatorCall: {
      method: "PUT",
      path: "/scenario-creator-calls/:id",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateScenarioCreatorCall>>>(),
      },
      query:
        c.type<
          Omit<Parameters<typeof updateScenarioCreatorCall>[0], "id" | "data">
        >(),
      body: c.type<
        Omit<Parameters<typeof updateScenarioCreatorCall>[0], "id" | "idType">
      >(),
    },
    // Public demo scenario creator: unauthenticated, form → generated,
    // moderated scenario scoped to the returned token (see personal-scenario).
    createDemoScenario: {
      method: "POST",
      path: "/demo/scenario-creator/scenarios",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createDemoScenario>>>(),
        400: c.type<{ message: string }>(),
        429: c.type<{ message: string }>(),
        500: c.type<{ message: string }>(),
      },
      body: c.type<Parameters<typeof createDemoScenario>[0]>(),
    },
    deleteScenario: {
      method: "DELETE",
      path: "/scenarios/:scenarioId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteScenario>>>(),
      },
      body: c.noBody(),
    },
    // ENG-1138 — the scenario editor's translations section (staff-only).
    getScenarioTranslationSummary: {
      method: "GET",
      path: "/admin/scenarios/:scenarioId/translations",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getScenarioTranslationSummary>>>(),
      },
    },
    startScenarioTranslation: {
      method: "POST",
      path: "/admin/scenarios/:scenarioId/translations",
      responses: {
        200: c.type<Awaited<ReturnType<typeof startScenarioTranslation>>>(),
      },
      body: c.type<{
        languageCode: Parameters<typeof startScenarioTranslation>[0]["languageCode"]
      }>(),
    },
    getTranslationsForScenario: {
      method: "GET",
      path: "/scenarios/:scenarioId/translations",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getTranslationsForScenario>>>(),
      },
    },
    sendSlackMessageToUsers: {
      method: "POST",
      path: "/scripts/send-slack-message-to-users",
      responses: {
        200: c.type<{ success: boolean; message: string }>(),
      },
      body: c.type<Parameters<typeof sendMessageToUsers>[0]>(),
    },
    generateInsightForOrg: {
      method: "POST",
      path: "/orgs/:orgId/insights/generate",
      responses: {
        200: c.type<Awaited<ReturnType<typeof generateInsightForOrg>>>(),
        400: c.type<{ message: string }>(),
        401: c.type<{ message: string }>(),
        403: c.type<{ message: string }>(),
        500: c.type<{ message: string }>(),
      },
      // Optional reporting filters so a generation can be scoped to a specific
      // /reports filter view. Derived from the function signature so the
      // contract stays in sync. The dashboard's filtered path uses
      // getInsightForFilters below (which serves from the store when it can);
      // this endpoint always generates.
      body: c.type<{
        filters?: Parameters<typeof generateInsightForOrg>[0]["filters"]
      } | null>(),
    },
    // Serve-or-generate the Learning Insights for a reporting-filter view.
    // Stored generations are reused org-wide (one per view per day) and
    // historical windows are always served from the store — see
    // getOrGenerateInsightForFilters in lib/insights.ts for the policy.
    getInsightForFilters: {
      method: "POST",
      path: "/orgs/:orgId/insights/for-view",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof getOrGenerateInsightForFilters>>
        >(),
        400: c.type<{ message: string }>(),
        401: c.type<{ message: string }>(),
        403: c.type<{ message: string }>(),
        500: c.type<{ message: string }>(),
      },
      body: c.type<{
        filters: Parameters<typeof getOrGenerateInsightForFilters>[0]["filters"]
      }>(),
    },
    getInsightsForOrg: {
      method: "GET",
      path: "/orgs/:orgId/insights",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getInsightsForOrg>>>(),
        400: c.type<{ message: string }>(),
        401: c.type<{ message: string }>(),
        403: c.type<{ message: string }>(),
        500: c.type<{ message: string }>(),
      },
    },
    generateInsightsForAllOrgs: {
      method: "POST",
      path: "/scripts/generate-insights-for-orgs",
      responses: {
        200: c.type<Awaited<ReturnType<typeof generateInsightsForAllOrgs>>>(),
      },
      body: c.noBody(),
    },
    generateCustomInsight: {
      method: "POST",
      path: "/scripts/generate-custom-insight",
      responses: {
        200: c.type<Awaited<ReturnType<typeof generateInsightForOrg>>>(),
        400: c.type<{ message: string }>(),
        401: c.type<{ message: string }>(),
        500: c.type<{ message: string }>(),
      },
      body: c.type<Parameters<typeof generateInsightForOrg>[0]>(),
    },
    createReminder: {
      method: "POST",
      path: "/reminders",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createReminder>>>(),
        400: c.type<{ message: string }>(),
      },
      body: c.type<
        Omit<
          Parameters<typeof createReminder>[0],
          "userId" | "email"
        >["data"] & {
          /** Optional email for anonymous/demo users. If not provided, uses authenticated user's email */
          email?: string
        }
      >(),
    },
    sendReminders: {
      method: "GET",
      path: "/scripts/send-reminders",
      responses: {
        200: c.type<Awaited<ReturnType<typeof sendReminders>>>(),
      },
    },
    generateOrgScenarioContent: {
      method: "POST",
      path: "/org-scenario/generate",
      responses: {
        200: c.type<Awaited<ReturnType<typeof generateScenario>>>(),
        500: c.type<{ message: string }>(),
      },
      body: c.type<
        OrgScenarioFormData & {
          // Reopened document-backed drafts: the canvas can't re-attach the
          // files (base64 is never persisted), so it sends the stored
          // documentAnalysis for the server to ground the regeneration on.
          // Ignored whenever the request carries real document content.
          persistedDocumentAnalysis?: string | null
        }
      >(),
    },
    regenerateOrgScenarioSection: {
      method: "POST",
      path: "/org-scenario/re-generate",
      responses: {
        200: c.type<Awaited<ReturnType<typeof regenerateScenarioPartial>>>(),
        500: c.type<{ message: string }>(),
      },
      body: c.type<{
        formData: OrgScenarioFormData
        sectionType: Exclude<
          keyof GeneratedContent,
          | "documentAnalysis"
          | "moderationVerdict"
          | "moderationReason"
          | "systemPrompt"
        >
        currentContent: GeneratedContent
      }>(),
    },
    submitOrgScenario: {
      method: "POST",
      path: "/org-scenario/submit",
      responses: {
        200: c.noBody(),
      },
      body: c.type<SubmissionData>(),
    },
    // Staff-only: run a feedback framework (any status) or raw prompt text
    // against a past call and return the structured report + rendered
    // markdown, without touching the call's stored feedback.
    feedbackTestBench: {
      method: "POST",
      path: "/admin/feedback-test-bench",
      responses: {
        200: c.type<Awaited<ReturnType<typeof runFeedbackTestBench>>>(),
      },
      body: c.type<{
        callId: string
        frameworkId?: string
        rawPrompt?: string
      }>(),
    },
    // Scenario Studio: an org admin's view of every custom scenario their org
    // has created, with lifecycle status. Org-admin gated in the route.
    getScenarioStudio: {
      method: "GET",
      path: "/orgs/:orgId/scenario-studio",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getScenarioStudioForOrg>>>(),
      },
    },
    // Studio canvas: categories the org can browse, for the category
    // suggestion dropdown. Org-admin gated in the route.
    getStudioCategories: {
      method: "GET",
      path: "/orgs/:orgId/studio-categories",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getStudioCategoriesForOrg>>>(),
      },
    },
    // Studio canvas: the org's pickable reference scenarios (continuity) —
    // live customs + editable drafts (badged), ids/titles only. Org-admin
    // gated in the route; reference CONTENT never leaves the server.
    getStudioReferenceScenarios: {
      method: "GET",
      path: "/orgs/:orgId/studio-reference-scenarios",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof getStudioReferenceScenariosForOrg>>
        >(),
      },
    },
    // Studio canvas: character list for the picker (any authed user).
    getStudioCharacters: {
      method: "GET",
      path: "/studio/characters",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getCharacters>>>(),
      },
    },
    // Studio canvas hydration: one draft (or submitted Studio scenario),
    // org-scoped — so moderation-flagged (hidden) drafts and colleagues'
    // drafts reopen correctly, both of which the learner-catalogue filter
    // deliberately drops. Org-admin gated in the route.
    getStudioDraft: {
      method: "GET",
      path: "/orgs/:orgId/studio-drafts/:scenarioId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getStudioDraftForOrg>>>(),
      },
    },
    // Studio canvas draft lifecycle — all org-admin gated in the routes.
    createStudioDraft: {
      method: "POST",
      path: "/orgs/:orgId/studio-drafts",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createStudioDraft>>>(),
      },
      body: c.type<{
        formData: OrgScenarioFormData
        content: GeneratedContent
      }>(),
    },
    updateStudioDraft: {
      method: "POST",
      path: "/orgs/:orgId/studio-drafts/:scenarioId/update",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateStudioDraft>>>(),
      },
      body: c.type<{
        formData: OrgScenarioFormData
        content: GeneratedContent
      }>(),
    },
    deleteStudioDraft: {
      method: "POST",
      path: "/orgs/:orgId/studio-drafts/:scenarioId/delete",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteStudioDraft>>>(),
      },
      body: c.noBody(),
    },
    submitStudioDraft: {
      method: "POST",
      path: "/orgs/:orgId/studio-drafts/:scenarioId/submit",
      responses: {
        200: c.type<Awaited<ReturnType<typeof submitStudioDraft>>>(),
      },
      // Optional canvas state: when provided the server persists it before
      // locking, so what enters review is exactly what was on screen (a
      // failed autosave flush can no longer submit stale content). Undefined
      // keeps the old submit-what-was-last-saved behaviour. `note` is the
      // submitter's optional message to the Ambr review team.
      body: c.type<
        | {
            formData: OrgScenarioFormData
            content: GeneratedContent
            note?: string
          }
        | undefined
      >(),
    },
    // Editing a live scenario: creates a fresh draft copying the live content
    // (kind=Edit on submission); the live record stays untouched until the
    // review approves the changes.
    createStudioEditDraft: {
      method: "POST",
      path: "/orgs/:orgId/studio-drafts/from-scenario/:scenarioId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createStudioEditDraft>>>(),
      },
      body: c.noBody(),
    },
    // Pronoun-aware character rename: fixes gendered language after a
    // character switch (the canvas does the name swap itself).
    renameStudioCharacter: {
      method: "POST",
      path: "/orgs/:orgId/studio/rename-character",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof renameStudioCharacterInContent>>
        >(),
      },
      body: c.type<{
        content: Parameters<typeof renameStudioCharacterInContent>[0]["content"]
        fromName: string
        // The picked roster character. Name, gender and the exact language
        // line are derived from the roster SERVER-side — the language line
        // is a safety property (accent → variant map + anti-imitation rule)
        // and must never be client-supplied.
        toCharacterId: string
        // Included when the switch changes gender/variant: the prompt gets
        // the same targeted pronoun rewrite + exact language line.
        systemPrompt?: string
      }>(),
    },
    // Studio polish step: skill-grade character prompt on the pipeline model
    // (slower + costlier than scaffold generation, so it's a separate call).
    generateStudioCharacterPrompt: {
      method: "POST",
      path: "/orgs/:orgId/studio/character-prompt",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof generateStudioCharacterPrompt>>
        >(),
      },
      body: c.type<{
        formData: OrgScenarioFormData
        content: GeneratedContent
        characterNotes?: string
        // Tweak mode: revise the current prompt from a free-text change
        // request instead of building from scratch.
        revision?: { currentSystemPrompt: string; tweak: string }
      }>(),
    },
    // ── Personal Scenario Creator (PSC v2) ──
    // Learner-gated versions of the Studio generation/editing stack: same lib
    // functions, no org parameter, no submit/approval lifecycle. All gated in
    // the routes by the org's personalScenarioCreator setting (ENG-1412).
    generatePersonalScenario: {
      method: "POST",
      path: "/personal-scenario/generate",
      responses: {
        200: c.type<Awaited<ReturnType<typeof generateScenario>>>(),
        ...personalScenarioErrorResponses,
        500: c.type<{ message: string }>(),
      },
      body: c.type<OrgScenarioFormData>(),
    },
    regeneratePersonalScenarioSection: {
      method: "POST",
      path: "/personal-scenario/re-generate",
      responses: {
        200: c.type<Awaited<ReturnType<typeof regenerateScenarioPartial>>>(),
        ...personalScenarioErrorResponses,
        500: c.type<{ message: string }>(),
      },
      body: c.type<{
        formData: OrgScenarioFormData
        sectionType: Exclude<
          keyof GeneratedContent,
          | "documentAnalysis"
          | "moderationVerdict"
          | "moderationReason"
          | "systemPrompt"
        >
        currentContent: GeneratedContent
      }>(),
    },
    // Character prompt polish (and the free-text tweak loop) for personal
    // scenarios — the same slow pipeline-model pass the Studio canvas uses.
    generatePersonalCharacterPrompt: {
      method: "POST",
      path: "/personal-scenario/character-prompt",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof generateStudioCharacterPrompt>>
        >(),
        ...personalScenarioErrorResponses,
      },
      body: c.type<{
        formData: OrgScenarioFormData
        content: GeneratedContent
        characterNotes?: string
        revision?: { currentSystemPrompt: string; tweak: string }
      }>(),
    },
    // Pronoun-aware character rename after a character switch (the editor does
    // the name swap itself, same as the Studio canvas).
    renamePersonalCharacter: {
      method: "POST",
      path: "/personal-scenario/rename-character",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof renameStudioCharacterInContent>>
        >(),
        ...personalScenarioErrorResponses,
      },
      body: c.type<{
        content: Parameters<typeof renameStudioCharacterInContent>[0]["content"]
        fromName: string
        toCharacterId: string
        systemPrompt?: string
      }>(),
    },
    createPersonalScenario: {
      method: "POST",
      path: "/personal-scenarios",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createPersonalScenario>>>(),
        ...personalScenarioErrorResponses,
      },
      body: c.type<{
        formData: OrgScenarioFormData
        content: GeneratedContent
        // Whether the character prompt still matches the content — persisted
        // so the stale/rebuild state survives a reload.
        characterPrompt?: { stale: boolean; needsRebuild: boolean }
      }>(),
    },
    updatePersonalScenario: {
      method: "POST",
      path: "/personal-scenarios/:scenarioId/update",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updatePersonalScenario>>>(),
        ...personalScenarioErrorResponses,
      },
      body: c.type<{
        formData: OrgScenarioFormData
        content: GeneratedContent
        // Whether the character prompt still matches the content — persisted
        // so the stale/rebuild state survives a reload.
        characterPrompt?: { stale: boolean; needsRebuild: boolean }
      }>(),
    },
    // Editor hydration: one personal scenario, owner-scoped — fetched here
    // instead of the learner catalogue so a moderation-hidden record can still
    // be reopened and fixed.
    getPersonalScenario: {
      method: "GET",
      path: "/personal-scenarios/:scenarioId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getPersonalScenarioForUser>>>(),
        ...personalScenarioErrorResponses,
      },
    },
    // ── Staff-only admin: scenario review queue ──
    getScenarioReviewQueue: {
      method: "GET",
      path: "/admin/scenario-review-queue",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getScenarioReviewQueue>>>(),
      },
    },
    approveScenarioSubmission: {
      method: "POST",
      path: "/admin/scenario-review/approve",
      responses: {
        200: c.type<Awaited<ReturnType<typeof approveScenarioSubmission>>>(),
      },
      body: c.type<{
        submissionId: string
        categoryIds: string[]
        extraWhitelistCmsOrgIds: number[]
        allowOtherOrgs: boolean
        notifyCustomer: boolean
        // Published framework to attach at approval; null/undefined leaves the
        // scenario on the normal feedback-resolution chain.
        feedbackFrameworkId?: string | null
        // Progress-tracking taxonomy — required for create approvals,
        // optional re-tag for edit approvals.
        callType?: string | null
        skillTags?: string[] | null
      }>(),
    },
    declineScenarioSubmission: {
      method: "POST",
      path: "/admin/scenario-review/decline",
      responses: {
        200: c.type<Awaited<ReturnType<typeof declineScenarioSubmission>>>(),
      },
      body: c.type<{ submissionId: string; reason: string }>(),
    },
    // Visibility explainer: why can('t) an org/user see a scenario — the
    // rule-by-rule trace mirroring the real visibility filter.
    explainScenarioVisibility: {
      method: "POST",
      path: "/admin/visibility-explain",
      responses: {
        200: c.type<Awaited<ReturnType<typeof explainScenarioVisibility>>>(),
      },
      body: c.type<{
        scenarioId: string
        cmsOrgId: number
        userEmail?: string
      }>(),
    },
    // Staff-scoped org list: the approve dialog's whitelist picker and the
    // framework editor's org selector need EVERY org — the user-scoped
    // cms-data payload doesn't carry other orgs.
    getAdminCmsOrgs: {
      method: "GET",
      path: "/admin/cms-orgs",
      responses: {
        200: c.type<{ orgs: { id: number; orgName: string }[] }>(),
      },
    },
    // ── Staff-only admin: feedback framework editor ──
    getAdminFeedbackFrameworks: {
      method: "GET",
      path: "/admin/feedback-frameworks",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getFrameworkLibrary>>>(),
      },
    },
    createAdminFeedbackFramework: {
      method: "POST",
      path: "/admin/feedback-frameworks",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createFeedbackFramework>>>(),
      },
      body: c.type<{ fields: Partial<FeedbackFramework> }>(),
    },
    updateAdminFeedbackFramework: {
      method: "POST",
      path: "/admin/feedback-frameworks/:id/update",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateFeedbackFramework>>>(),
        400: c.type<{ message: string }>(),
      },
      body: c.type<{ fields: Partial<FeedbackFramework> }>(),
    },
    publishAdminFeedbackFramework: {
      method: "POST",
      path: "/admin/feedback-frameworks/:id/publish",
      responses: {
        200: c.type<Awaited<ReturnType<typeof publishFeedbackFramework>>>(),
        400: c.type<{ message: string }>(),
      },
      body: c.noBody(),
    },
    deleteAdminFeedbackFramework: {
      method: "POST",
      path: "/admin/feedback-frameworks/:id/delete",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof deleteFeedbackFrameworkChecked>>
        >(),
      },
      body: c.type<{ confirmPopulated?: boolean }>(),
    },
    previewFeedbackFramework: {
      method: "POST",
      path: "/admin/feedback-frameworks/preview",
      responses: {
        200: c.type<ReturnType<typeof previewFeedbackFramework>>(),
      },
      body: c.type<{ framework: FeedbackFramework }>(),
    },
    generateFeedbackFrameworkDraft: {
      method: "POST",
      path: "/admin/feedback-frameworks/generate",
      responses: {
        200: c.type<Awaited<ReturnType<typeof generateFrameworkDraft>>>(),
        400: c.type<{ message: string }>(),
        502: c.type<{ message: string }>(),
      },
      // Service validates after staff authorization, so malformed material is
      // never reflected by the generic transport validation error handler.
      body: c.type<FrameworkGenerationInput>(),
    },
    // Set/clear where a framework applies as a default: platform-wide,
    // personal scenarios, per-org or per-category. Staff-only.
    setFrameworkMapping: {
      method: "POST",
      path: "/admin/feedback-frameworks/mappings",
      responses: {
        200: c.type<Awaited<ReturnType<typeof setFrameworkMapping>>>(),
      },
      body: c.type<{
        target: FrameworkMappingTarget
        frameworkId: string | null
      }>(),
    },
    getRecentCallsForBench: {
      method: "GET",
      path: "/admin/bench-calls",
      query: c.type<{ scenarioId?: string; cmsOrgId?: string }>(),
      responses: {
        200: c.type<Awaited<ReturnType<typeof getRecentCallsForBench>>>(),
      },
    },
    // ── ENG-1138: unified content editor (staff-only) ──
    // One set of endpoints for every entity in contentEntities, so adding an
    // entity is a registry change rather than five new routes.
    listContent: {
      method: "GET",
      path: "/admin/content/:entity",
      // ENG-1138 access: optional org filter for scenarios/categories — keep
      // only what `orgId` can see. Ignored for other entities.
      query: c.type<{ orgId?: string }>(),
      responses: {
        200: c.type<Awaited<ReturnType<typeof listContent>>>(),
        ...contentErrorResponses,
      },
    },
    getContentRecord: {
      method: "GET",
      path: "/admin/content/:entity/:id",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getContentRecord>>>(),
        ...contentErrorResponses,
      },
    },
    getContentVersions: {
      method: "GET",
      path: "/admin/content/:entity/:id/versions",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getContentVersions>>>(),
        ...contentErrorResponses,
      },
    },
    saveContentRecord: {
      method: "POST",
      path: "/admin/content/:entity/save",
      responses: {
        200: c.type<Awaited<ReturnType<typeof saveContentRecord>>>(),
        ...contentErrorResponses,
      },
      // `id` omitted = create. `expectedRevision` is the revision the editor
      // loaded; a save against a row that changed since is refused with 409.
      body: c.type<{
        id?: string
        fields: Record<string, unknown>
        expectedRevision?: string
      }>(),
    },
    // ENG-1138 — staff access-control editor. Rules live outside the content
    // editor: they have no versions and a subject/target pair rather than a
    // field list.
    getAccessControlData: {
      method: "GET",
      path: "/admin/access-control",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getAccessControlData>>>(),
      },
    },
    getAccessControlSubjectsForOrg: {
      method: "GET",
      path: "/admin/access-control/orgs/:orgId/subjects",
      // Users are searched server-side by email or name; groups ship whole.
      query: c.type<{ query?: string }>(),
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof getAccessControlSubjectsForOrg>>
        >(),
      },
    },
    previewAccessForUser: {
      method: "GET",
      path: "/admin/access-control/users/:userId/preview",
      responses: {
        200: c.type<Awaited<ReturnType<typeof previewAccessForUser>>>(),
      },
    },
    searchScenariosForAcl: {
      method: "GET",
      path: "/admin/access-control/scenarios",
      query: c.type<{ query?: string }>(),
      responses: {
        200: c.type<Awaited<ReturnType<typeof searchScenariosForAcl>>>(),
      },
    },
    saveAccessControlEntry: {
      method: "POST",
      path: "/admin/access-control/save",
      responses: {
        200: c.type<Awaited<ReturnType<typeof saveAccessControlEntry>>>(),
        // A malformed rule or an id that points at nothing.
        400: c.type<{ message: string }>(),
        404: c.type<{ message: string }>(),
      },
      // Parsed, not just typed: these rows decide who sees what, so a priority
      // outside the range or a fractional one is refused at the door.
      // assertWellFormed repeats the checks for callers that skip the route.
      body: z.object({
        // Omitted = create
        id: z.string().optional(),
        effect: z.enum(["ALLOW", "DENY"]),
        target: z.enum([
          "CoreScenarios",
          "CoreCategories",
          "AllScenarios",
          "AllCategories",
          "Scenario",
          "Category",
        ]),
        priority: z.number().int().min(aclPriorityMin).max(aclPriorityMax),
        scenarioId: z.string().nullish(),
        categoryId: z.string().nullish(),
        orgId: z.number().int().nullish(),
        teamId: z.string().nullish(),
        userId: z.number().int().nullish(),
      }),
    },
    deleteAccessControlEntry: {
      method: "POST",
      path: "/admin/access-control/:id/delete",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteAccessControlEntry>>>(),
        404: c.type<{ message: string }>(),
      },
      body: c.noBody(),
    },
    // Every rule naming one scenario or category — the Access panel on the
    // content editor. Same rows as the screen above, same save/delete.
    getAccessForContentItem: {
      method: "GET",
      path: "/admin/access-control/items/:kind/:id",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getAccessForContentItem>>>(),
        400: c.type<{ message: string }>(),
        404: c.type<{ message: string }>(),
      },
    },
    // ENG-1138 — consent screen for the staff MCP connector. Claude's
    // /authorize request is parked server-side and the browser lands on
    // /mcp/authorize?request=<id>; these read, approve or deny it.
    getMcpAuthorizationRequest: {
      method: "GET",
      path: "/admin/mcp/authorizations/:id",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getMcpAuthorizationRequest>>>(),
        400: c.type<{ message: string }>(),
      },
    },
    approveMcpAuthorization: {
      method: "POST",
      path: "/admin/mcp/authorizations/:id/approve",
      responses: {
        200: c.type<{ redirectUrl: string }>(),
        400: c.type<{ message: string }>(),
      },
      body: c.noBody(),
    },
    denyMcpAuthorization: {
      method: "POST",
      path: "/admin/mcp/authorizations/:id/deny",
      responses: {
        200: c.type<{ redirectUrl: string }>(),
        400: c.type<{ message: string }>(),
      },
      body: c.noBody(),
    },
    // Voice options for the character editor's pickers. Deliberately NOT under
    // /admin/content/: GET /admin/content/:entity is declared first and would
    // match "voices" as an entity name, so parseContentEntity would reject the
    // request before it ever reached this handler.
    getVoiceOptions: {
      method: "GET",
      path: "/admin/voices",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getVoiceOptions>>>(),
      },
    },
    uploadContentAsset: {
      method: "POST",
      path: "/admin/content-assets",
      responses: {
        200: c.type<Awaited<ReturnType<typeof uploadContentAsset>>>(),
        ...contentErrorResponses,
      },
      body: c.type<Parameters<typeof uploadContentAsset>[0]>(),
    },
    duplicateContentRecord: {
      method: "POST",
      path: "/admin/content/:entity/:id/duplicate",
      responses: {
        200: c.type<Awaited<ReturnType<typeof duplicateContentRecord>>>(),
        ...contentErrorResponses,
      },
      body: c.noBody(),
    },
    restoreContentVersion: {
      method: "POST",
      path: "/admin/content/:entity/versions/:versionId/restore",
      responses: {
        200: c.type<Awaited<ReturnType<typeof restoreContentVersion>>>(),
        ...contentErrorResponses,
      },
      body: c.noBody(),
    },
    // ENG-1138 — migration verification: the same data fetched the old way and
    // the new way, diffed.
    verifyContentMigration: {
      method: "GET",
      path: "/admin/migration-verify/content",
      responses: {
        200: c.type<Awaited<ReturnType<typeof verifyContentMigration>>>(),
      },
    },
    verifyAccessForUser: {
      method: "GET",
      path: "/admin/migration-verify/users/:userId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof verifyAccessForUser>>>(),
      },
    },
    getVerificationSubjects: {
      method: "GET",
      path: "/admin/migration-verify/subjects",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getVerificationSubjects>>>(),
      },
    },
    // Off the /admin/content/:entity/… tree on purpose, alongside
    // /admin/content-assets. GET /admin/content/:entity/:id is registered
    // above, so a literal segment in that position is swallowed as an id —
    // /admin/content/scenarios/export resolved to getContentRecord and failed
    // with "No Scenario with id export". Reordering would have fixed the
    // symptom and left the trap for the next literal route added here.
    exportContent: {
      method: "GET",
      path: "/admin/content-export/:entity",
      // Comma-separated ids. Omitted exports the whole entity, which for
      // scenarios is 1,800 records and ~14 MB in one uncompressed response.
      query: c.type<{ ids?: string }>(),
      responses: {
        200: c.type<Awaited<ReturnType<typeof exportContent>>>(),
        ...contentErrorResponses,
      },
    },
    importContent: {
      method: "POST",
      path: "/admin/content-import/:entity",
      responses: {
        200: c.type<Awaited<ReturnType<typeof importContent>>>(),
        ...contentErrorResponses,
      },
      body: c.type<{ records: Record<string, unknown>[] }>(),
    },
    deleteContentRecord: {
      method: "POST",
      path: "/admin/content/:entity/:id/delete",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteContentRecord>>>(),
        ...contentErrorResponses,
      },
      body: c.noBody(),
    },
    // Spreadsheet export, alongside the JSON one and off the
    // /admin/content/:entity/… tree for the same shadowing reason. Returned as
    // JSON-wrapped text so the existing client and download path apply.
    exportContentCsv: {
      method: "GET",
      path: "/admin/content-export-csv/:entity",
      query: c.type<{ ids?: string }>(),
      responses: {
        200: c.type<Awaited<ReturnType<typeof exportContentCsv>>>(),
        ...contentErrorResponses,
      },
    },
    // The scenarios using a character, category or skill ("Used by").
    getContentUsage: {
      method: "GET",
      path: "/admin/content-usage/:entity/:id",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getContentUsage>>>(),
        ...contentErrorResponses,
      },
    },
    // The Core catalogue screen: every scenario/category with its isCore flag.
    getCoreCatalogue: {
      method: "GET",
      path: "/admin/content-core",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getCoreCatalogue>>>(),
        ...contentErrorResponses,
      },
    },
    setCoreFlag: {
      method: "POST",
      path: "/admin/content-core/set",
      responses: {
        200: c.type<Awaited<ReturnType<typeof setCoreFlag>>>(),
        ...contentErrorResponses,
      },
      body: c.type<{
        entity: "scenarios" | "categories"
        id: string
        isCore: boolean
      }>(),
    },
    // ── Early access (Labs) registry ──
    // The Labs page list, already filtered server-side to what the session
    // user may see (kill switch, staff bypass, visibility + org gates).
    getEarlyAccessFeatures: {
      method: "GET",
      path: "/early-access-features",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof getEarlyAccessFeaturesForUser>>
        >(),
      },
    },
    // Staff-only editor: every row (incl. disabled) + Postgres org list for
    // the org-access picker
    getAdminEarlyAccessFeatures: {
      method: "GET",
      path: "/admin/early-access-features",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getAdminEarlyAccessFeatures>>>(),
      },
    },
    createAdminEarlyAccessFeature: {
      method: "POST",
      path: "/admin/early-access-features",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createEarlyAccessFeature>>>(),
        // Staff-input validation failure (blank required field, bad URL)
        400: c.type<{ message: string }>(),
      },
      body: c.type<{ fields: EarlyAccessFeatureFields }>(),
    },
    updateAdminEarlyAccessFeature: {
      method: "POST",
      path: "/admin/early-access-features/:id/update",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateEarlyAccessFeature>>>(),
        // Staff-input validation failure or malformed :id
        400: c.type<{ message: string }>(),
      },
      body: c.type<{ fields: Partial<EarlyAccessFeatureFields> }>(),
    },
    deleteAdminEarlyAccessFeature: {
      method: "POST",
      path: "/admin/early-access-features/:id/delete",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteEarlyAccessFeature>>>(),
        // Malformed :id, missing feature, or refused while still enabled
        400: c.type<{ message: string }>(),
      },
      body: c.noBody(),
    },
    duplicateAdminEarlyAccessFeature: {
      method: "POST",
      path: "/admin/early-access-features/:id/duplicate",
      responses: {
        200: c.type<Awaited<ReturnType<typeof duplicateEarlyAccessFeature>>>(),
        // Malformed :id or missing feature
        400: c.type<{ message: string }>(),
      },
      body: c.noBody(),
    },
    getTeams: {
      method: "GET",
      path: "/orgs/:orgId/teams",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getTeams>>>(),
      },
    },
    // ENG-1521: the org-BROWSABLE catalogue for the default-onboarding-scenario
    // pickers (org settings, group settings, group creation): the org-level
    // filter narrowed to scenarios with at least one org-visible category, i.e.
    // what the org's learners actually see on their scenario screens (excludes
    // the coaching carve-outs and hidden-category scenarios). Deliberately not
    // the admin's own cms-data: group content-access rules govern learner
    // browsing, and an admin may configure a group whose categories they don't
    // share. Same computation as the server-side write validation, so the
    // picker and the validation can never disagree.
    getOnboardingScenarioOptions: {
      method: "GET",
      path: "/orgs/:orgId/onboarding-scenario-options",
      responses: {
        200: c.type<{
          scenarios: {
            // Opaque scenario id (ENG-1138).
            id: string
            title: string
            listingTitle: string
            categoryNames: string[]
          }[]
        }>(),
      },
    },
    // The org's catalogue for the group content-access editors (GroupDetail,
    // TeamPermissions): every category and browsable scenario the ORG can see.
    // Deliberately not the admin's own cms-data, which is narrowed by the
    // admin's own group memberships — an admin in a restricted group could
    // otherwise only offer (and only display) the categories they personally
    // see, so other groups' selections rendered as an empty list.
    getContentAccessOptions: {
      method: "GET",
      path: "/orgs/:orgId/content-access-options",
      responses: {
        200: c.type<{
          categories: { id: string; name: string }[]
          scenarios: {
            // Opaque scenario id (ENG-1138).
            id: string
            title: string
            // Name of the scenario's first org-visible category.
            categoryName: string
          }[]
        }>(),
      },
    },
    getTeam: {
      method: "GET",
      path: "/orgs/:orgId/teams/:teamId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getTeam>>>(),
      },
    },
    createTeam: {
      method: "POST",
      path: "/orgs/:orgId/teams",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createTeam>>>(),
      },
      // ENG-1521: defaultScenarioId = optional group onboarding default,
      // settable at creation; validated server-side against the org catalogue.
      // Scenario ids are opaque strings (ENG-1138); coerceDefaultScenarioIdInput
      // still stringifies a legacy integer at runtime.
      body: c.type<{ name: string; defaultScenarioId?: string | null }>(),
    },
    updateTeam: {
      method: "PUT",
      path: "/orgs/:orgId/teams/:teamId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateTeam>>>(),
      },
      body: c.type<
        Omit<Parameters<typeof updateTeam>[0], "teamId" | "orgId">
      >(),
    },
    deleteTeam: {
      method: "DELETE",
      path: "/orgs/:orgId/teams/:teamId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteTeam>>>(),
      },
      body: c.noBody(),
    },
    addUsersToTeam: {
      method: "POST",
      path: "/orgs/:orgId/teams/:teamId/users",
      responses: {
        200: c.type<Awaited<ReturnType<typeof addUsersToTeam>>>(),
      },
      body: c.type<{ userIds?: number[]; emails?: string[] }>(),
    },
    removeUsersFromTeam: {
      method: "DELETE",
      path: "/orgs/:orgId/teams/:teamId/users",
      responses: {
        200: c.type<Awaited<ReturnType<typeof removeUsersFromTeam>>>(),
      },
      body: c.type<{ userIds: number[] }>(),
    },
    executeRetentionPolicy: {
      method: "POST",
      path: "/scripts/execute-retention-policy",
      responses: {
        200: c.type<Awaited<ReturnType<typeof executeRetentionPolicy>>>(),
      },
      body: c.noBody(),
    },
    // Generates analysis for orphaned calls: Live API calls where the user
    // left without clicking "Leave meeting", and Vapi calls whose end-of-call
    // webhook died before analysis (ENG-1570).
    // Called by Cloud Scheduler every minute
    processOrphanedCalls: {
      method: "POST",
      path: "/scripts/process-orphaned-calls",
      responses: {
        200: c.type<Awaited<ReturnType<typeof processOrphanedCalls>>>(),
      },
      body: c.noBody(),
    },
    // One-off backfill copying historic Vapi calls' server audio URL from the
    // `vapiCall` JSON payload into the first-class `recordingUrl` column, so
    // pre-ENG-1214 calls get the same history badge + recording dialog as new
    // ones (the webhook writes the column for every new call). Idempotent —
    // only touches rows whose `recordingUrl` is null. Invoked manually after
    // deploy, not scheduled.
    backfillVapiRecordingUrls: {
      method: "POST",
      path: "/scripts/backfill-vapi-recording-urls",
      responses: {
        200: c.type<Awaited<ReturnType<typeof backfillVapiRecordingUrls>>>(),
      },
      body: c.noBody(),
    },
    // Generates a persistent login token for a user (called from Retool)
    generateLoginToken: {
      method: "POST",
      path: "/scripts/generate-login-token",
      responses: {
        200: c.type<{ token: string; loginUrl: string }>(),
        404: c.type<{ message: string }>(),
      },
      body: z.object({
        userId: z.number(),
        createdBy: z.string(),
      }),
    },
    archiveUser: {
      method: "POST",
      path: "/users/:userId/archive",
      responses: {
        200: c.type<Awaited<ReturnType<typeof archiveUser>>>(),
      },
      body: c.noBody(),
    },
    // Daily cron: record active user count snapshots for every non-archived org
    recordUserCountSnapshots: {
      method: "POST",
      path: "/scripts/record-user-count-snapshots",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof recordUserCountSnapshotsForAllOrgs>>
        >(),
      },
      body: c.noBody(),
    },
    // Hourly cron: archive inactive over-limit users for every floating-license
    // org so the reported active user count stays accurate between logins
    makeRoomForFloatingLicenses: {
      method: "POST",
      path: "/scripts/make-room-for-floating-licenses",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof makeRoomForFloatingLicenseOrgs>>
        >(),
      },
      body: c.noBody(),
    },
    // Daily cron: delete orphaned presentation/Q&A recording prefixes that no
    // Presentation row references (abandoned mid-session captures). Send `{}`
    // for a real run or `{"dryRun": true}` to only report.
    sweepOrphanedRecordings: {
      method: "POST",
      path: "/scripts/sweep-orphaned-recordings",
      responses: {
        200: c.type<Awaited<ReturnType<typeof sweepOrphanedRecordings>>>(),
      },
      body: z.object({ dryRun: z.boolean().optional() }),
    },
    generateTranslations: {
      method: "POST",
      path: "/scripts/generate-translations",
      responses: {
        200: c.noBody(),
      },
      body: c.type<Parameters<typeof generateAllTranslations>[0]>(),
    },
    reportTranslationContentChange: {
      method: "POST",
      path: "/scripts/report-translation-content-change",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof reportTranslationContentChange>>
        >(),
      },
      body: c.type<Parameters<typeof reportTranslationContentChange>[0]>(),
    },
    // Retool-authenticated endpoint to toggle the outage banner on/off
    updateOutageBanner: {
      method: "POST",
      path: "/scripts/update-outage-banner",
      responses: {
        200: z.object({
          enabled: z.boolean(),
          message: z.string(),
        }),
      },
      body: z.object({
        enabled: z.boolean(),
        message: z.string().optional(),
      }),
    },
    getAnamSessionToken: {
      method: "POST",
      path: "/anam/session-token",
      body: z
        .object({
          // Opaque string character id (ENG-1138). A positive integer is still
          // accepted and stringified here, once, so a browser holding the
          // pre-ENG-1138 bundle during rollout (frontend and API deploy
          // separately) doesn't get a 400 mid sales call. Everything past this
          // boundary sees a string.
          characterId: z
            .union([z.string().min(1), z.number().int().positive()])
            .transform((id) => String(id)),
          // Demo link the visitor is on. Only honoured for allowlisted demo
          // orgs (anamVideoAvatarDemoOrgIds); never widens access otherwise.
          demoOrgId: z.string().optional(),
        })
        .strict(),
      responses: {
        200: z.object({ sessionToken: z.string(), avatarId: z.string() }),
        400: z.object({ error: z.string() }),
        401: z.object({ error: z.string() }),
        403: z.object({ error: z.string() }),
        404: z.object({ error: z.string() }),
        429: z.object({ error: z.string() }),
        502: z.object({ error: z.string() }),
        503: z.object({ error: z.string() }),
      },
    },
    getLiveApiEphemeralToken: {
      method: "POST",
      path: "/call/session-token",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getLiveApiEphemeralToken>>>(),
      },
      body: c.noBody(),
    },
    // Network diagnostics (public, rate-limited server-side). Phase 0: the
    // in-call collector posts one compact connection summary per call attempt.
    recordCallDiagnostic: {
      method: "POST",
      path: "/diagnostics/call",
      responses: {
        200: z.object({ id: z.string() }),
        429: z.object({ message: z.string() }),
      },
      body: z.object({
        // Internal Call.uuid when a row exists; absent for attempts that
        // failed before a call record was created
        callId: z.string().optional(),
        provider: z.enum(["Vapi", "GoogleLiveAPI"]),
        outcome: z.enum(["connected", "connect-failed", "ended"]),
        summary: z.record(z.any()),
      }),
    },
    // Phase 1: store one run of the public /network-check page; the server
    // stamps its own egress fingerprint (ASN, suspected proxy vendor) on it.
    recordNetworkCheck: {
      method: "POST",
      path: "/diagnostics/network-check",
      responses: {
        200: z.object({ id: z.string() }),
        429: z.object({ message: z.string() }),
      },
      body: z.object({
        email: z.string().optional(),
        orgName: z.string().optional(),
        results: z.record(z.any()),
      }),
    },
    // Fetch a stored network-check run for the shareable result link
    getNetworkCheckResult: {
      method: "GET",
      path: "/diagnostics/network-check/:id",
      responses: {
        200: c.type<
          NonNullable<Awaited<ReturnType<typeof getNetworkCheckResult>>>
        >(),
        404: z.object({ message: z.string() }),
      },
    },
    // What our API observes about the caller's network path: egress IP
    // (truncated), ASN + AS organisation, and proxy-identifying headers
    getDiagnosticsEcho: {
      method: "GET",
      path: "/diagnostics/echo",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getEgressInfo>>>(),
      },
    },
    // Returns all referral activity counters from the Value table
    getReferralActivities: {
      method: "GET",
      path: "/referral-activities",
      responses: {
        200: c.type<Array<{ key: string; value: string }>>(),
      },
    },
    // Increments a referral activity counter in the Value table
    incrementReferralActivity: {
      method: "POST",
      path: "/referral-activities/increment",
      responses: {
        200: c.type<{ key: string; value: string }>(),
      },
      body: z.object({
        key: z.enum([
          "ReferralActivityUserOrgReferralRequested",
          "ReferralActivityUserLinkedinRequested",
          "ReferralActivityUserQuoteRequested",
          "ReferralActivityAdminOrgReferralRequested",
          "ReferralActivityAdminLinkedinRequested",
          "ReferralActivityAdminCaseStudyRequested",
          "ReferralActivityAdminCommunityPostRequested",
          "ReferralActivityAdminQuoteRequested",
        ] as const satisfies readonly KeyName[]),
      }),
    },
    // Media interview simulator — suggest three key messages from a situation
    // + user role. Fast Gemini Flash call, used by the "Suggest" button on the
    // context form. No auth, same public-endpoint pattern as the other media
    // interview endpoints.
    suggestMediaInterviewMessages: {
      method: "POST",
      path: "/media-interview/suggest-messages",
      responses: {
        200: c.type<Awaited<ReturnType<typeof suggestKeyMessages>>>(),
        400: c.type<{ message: string }>(),
        500: c.type<{ message: string }>(),
      },
      body: z.object({
        situation: z.string().min(1).max(2000),
        userRole: z.string().min(1).max(200),
      }),
    },
    // Media interview simulator — generate fake news coverage from a transcript.
    // Public endpoint (no auth) — used by the /media-interview demo route.
    generateMediaCoverage: {
      method: "POST",
      path: "/media-interview/generate-coverage",
      responses: {
        200: c.type<Awaited<ReturnType<typeof generateMediaCoverage>>>(),
        400: c.type<{ message: string }>(),
        500: c.type<{ message: string }>(),
      },
      // Length caps protect the public endpoint from being used to pump
      // megabytes of text into the Claude prompt. Mirror media-coverage.ts.
      body: z.object({
        context: z.object({
          situation: z.string().min(1).max(2000),
          userRole: z.string().min(1).max(200),
          keyMessages: z.array(z.string().min(1).max(300)).min(1).max(5),
        }),
        journalist: z.object({
          name: z.string().min(1).max(120),
          outletName: z.string().max(120).nullable(),
          outletStyle: z.enum([
            "broadsheet",
            "tabloid",
            "trade",
            "broadcast",
            "online",
          ]),
          styleTraits: z.array(z.string().max(120)).max(10),
        }),
        format: z.enum(["tv", "radio", "print"]),
        transcript: z
          .array(
            z.object({
              role: z.enum(["user", "assistant"]),
              text: z.string().max(2000),
            }),
          )
          .min(1)
          .max(400),
      }),
    },
    // Render the generated articles as full newspaper-page IMAGES via Nano
    // Banana Pro. Runs after generateMediaCoverage returns; each article is
    // rendered in parallel. Failures per-article surface as `imageBase64: null`.
    generateMediaNewspaperImages: {
      method: "POST",
      path: "/media-interview/generate-newspaper-images",
      responses: {
        200: c.type<Awaited<ReturnType<typeof generateNewspaperImages>>>(),
        400: c.type<{ message: string }>(),
        500: c.type<{ message: string }>(),
      },
      body: z.object({
        articles: z
          .array(
            z.object({
              outletName: z.string().min(1).max(120),
              outletStyle: z.enum([
                "broadsheet",
                "tabloid",
                "trade",
                "broadcast",
                "online",
              ]),
              headline: z.string().min(1).max(300),
              subheading: z.string().max(300),
              byline: z.string().min(1).max(200),
              bodyMarkdown: z.string().min(1).max(4000),
              toneTag: z.enum(["favourable", "neutral", "critical"]),
            }),
          )
          .min(1)
          .max(4),
      }),
    },
    // TTS Feedback Podcast endpoint - generates audio from call feedback
    generateFeedbackPodcast: {
      method: "POST",
      path: "/calls/:callId/tts-podcast",
      responses: {
        200: c.type<Awaited<ReturnType<typeof generateFeedbackPodcast>>>(),
        400: c.type<{ message: string }>(),
        404: c.type<{ message: string }>(),
        500: c.type<{ message: string }>(),
      },
      body: c.noBody(),
    },

    // Multi-participant simulation (several AI personas in one meeting).
    // Both endpoints require an authenticated session — the group-sims beta
    // lives behind the dashboard. Length caps protect them from being used
    // to pump megabytes into LLM prompts.
    chooseMultiSimSpeaker: {
      method: "POST",
      path: "/multi-sim/next-speaker",
      responses: {
        200: c.type<Awaited<ReturnType<typeof chooseMultiSimSpeaker>>>(),
        500: c.type<{ message: string }>(),
      },
      body: z.object({
        personas: z
          .array(
            z.object({
              id: z.string().min(1).max(60),
              name: z.string().min(1).max(120),
              role: z.string().min(1).max(200),
              personality: z.string().min(1).max(600),
            }),
          )
          .min(2)
          .max(4),
        transcript: z
          .array(
            z.object({
              role: z.enum(["user", "bot"]),
              speakerName: z.string().min(1).max(120),
              message: z.string().max(2000),
            }),
          )
          .max(40),
        trigger: z.enum(["user_turn", "after_persona", "user_silent"]),
        lastSpeakerId: z.string().max(60).nullable(),
      }),
    },
    analyzeMultiSimTranscript: {
      method: "POST",
      path: "/multi-sim/analysis",
      responses: {
        200: c.type<Awaited<ReturnType<typeof analyzeMultiSimTranscript>>>(),
        500: c.type<{ message: string }>(),
      },
      body: z.object({
        scenario: z.object({
          title: z.string().min(1).max(200),
          context: z.string().min(1).max(4000),
          objectives: z.array(z.string().min(1).max(400)).min(1).max(8),
          // The trainee's briefing facts — lets the feedback judge what the
          // trainee did with the information they actually had
          keyDetails: z.array(z.string().min(1).max(600)).max(12).optional(),
        }),
        personas: z
          .array(
            z.object({
              id: z.string().min(1).max(60),
              name: z.string().min(1).max(120),
              role: z.string().min(1).max(200),
              personality: z.string().min(1).max(600),
            }),
          )
          .min(2)
          .max(4),
        // Sized for a realistic long meeting (the client truncates to match) —
        // generous caps would turn this into a free-form Claude prompt pump
        transcript: z
          .array(
            z.object({
              role: z.enum(["user", "bot"]),
              speakerName: z.string().min(1).max(120),
              message: z.string().max(2000),
            }),
          )
          .min(1)
          .max(200),
        durationSeconds: z
          .number()
          .min(0)
          .max(24 * 3600),
      }),
    },

    // ========================================================================
    // Journey Endpoints (Learning Paths)
    // ========================================================================

    // Get all journeys for an org (admin view)
    getJourneys: {
      method: "GET",
      path: "/orgs/:orgId/journeys",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getJourneys>>>(),
      },
      query: z
        .object({
          includeArchived: z.string().optional(),
        })
        .optional(),
    },

    // Get a single journey by ID
    getJourney: {
      method: "GET",
      path: "/journeys/:journeyId",
      responses: {
        200: c.type<NonNullable<Awaited<ReturnType<typeof getJourney>>>>(),
        404: c.type<{ message: string }>(),
      },
    },

    // Create a new journey
    createJourney: {
      method: "POST",
      path: "/orgs/:orgId/journeys",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createJourney>>>(),
        400: c.type<{ message: string }>(),
      },
      body: z.object({
        name: z.string().min(1),
        description: z.string().nullable().optional(),
        // Ordered steps with optional per-step difficulty (preferred shape)
        scenarios: z
          .array(
            z.object({
              scenarioId: z.string(),
              // null/omitted = any difficulty, "internal.standard" = Standard
              // only, else a CMS levelName ("" is never valid)
              difficultyLevel: z.string().min(1).nullable().optional(),
            }),
          )
          .optional(),
        // Deprecated: plain scenario IDs without difficulty (older clients)
        scenarioIds: z.array(z.string()).optional(),
        // When true, attempting a step enough times counts as completing it
        // (omitted = false: a successful call is required)
        acceptScenarioAttemptsAsCompletion: z.boolean().optional(),
        // Attempts that complete a step; only consulted when the toggle is on
        scenarioAttemptsRequired: z.number().int().min(1).optional(),
      }),
    },

    // Update a journey
    updateJourney: {
      method: "PUT",
      path: "/journeys/:journeyId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateJourney>>>(),
        404: c.type<{ message: string }>(),
      },
      body: z.object({
        name: z.string().min(1).optional(),
        description: z.string().nullable().optional(),
        // Ordered steps with optional per-step difficulty (preferred shape)
        scenarios: z
          .array(
            z.object({
              scenarioId: z.string(),
              // null/omitted = any difficulty, "internal.standard" = Standard
              // only, else a CMS levelName ("" is never valid)
              difficultyLevel: z.string().min(1).nullable().optional(),
            }),
          )
          .optional(),
        // Deprecated: plain scenario IDs without difficulty (older clients)
        scenarioIds: z.array(z.string()).optional(),
        // When true, attempting a step enough times counts as completing it
        acceptScenarioAttemptsAsCompletion: z.boolean().optional(),
        // Attempts that complete a step; only consulted when the toggle is on
        scenarioAttemptsRequired: z.number().int().min(1).optional(),
      }),
    },

    // Generate a journey name or description using AI
    generateJourneyContent: {
      method: "POST",
      path: "/journeys/generate-content",
      responses: {
        200: c.type<Awaited<ReturnType<typeof generateJourneyContent>>>(),
        400: c.type<{ message: string }>(),
      },
      body: z.object({
        field: z.enum(["name", "description"]),
        scenarioTitles: z.array(z.string()).min(1),
      }),
    },

    // Archive a journey (soft delete)
    archiveJourney: {
      method: "POST",
      path: "/journeys/:journeyId/archive",
      responses: {
        200: c.type<Awaited<ReturnType<typeof archiveJourney>>>(),
        404: c.type<{ message: string }>(),
      },
      body: c.noBody(),
    },

    // Restore an archived journey
    restoreJourney: {
      method: "POST",
      path: "/journeys/:journeyId/restore",
      responses: {
        200: c.type<Awaited<ReturnType<typeof restoreJourney>>>(),
        404: c.type<{ message: string }>(),
      },
      body: c.noBody(),
    },

    // Get all assignments for a journey (admin view)
    getJourneyAssignments: {
      method: "GET",
      path: "/journeys/:journeyId/assignments",
      responses: {
        200: c.type<
          NonNullable<Awaited<ReturnType<typeof getJourneyAssignments>>>
        >(),
        404: c.type<{ message: string }>(),
      },
    },

    // Assign a journey to users or a team
    createJourneyAssignment: {
      method: "POST",
      path: "/journeys/:journeyId/assignments",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createJourneyAssignment>>>(),
        400: c.type<{ message: string }>(),
        404: c.type<{ message: string }>(),
      },
      body: z.object({
        // Assign to specific users (by userId)
        userIds: z.array(z.number()).optional(),
        // Or assign to entire team (creates a team-level assignment; members inherit it)
        teamId: z.string().optional(),
        dueDate: z.string().nullable().optional(),
      }),
    },

    // Bulk-update the due date for one or more journey assignments
    bulkUpdateJourneyAssignmentDueDates: {
      method: "PATCH",
      path: "/journeys/:journeyId/assignments/due-date",
      responses: {
        200: c.type<
          Awaited<ReturnType<typeof bulkUpdateJourneyAssignmentDueDates>>
        >(),
        400: c.type<{ message: string }>(),
        404: c.type<{ message: string }>(),
      },
      body: z.object({
        assignmentIds: z.array(z.string()).min(1),
        dueDate: z.string().nullable(),
      }),
    },

    // Remove a journey assignment
    deleteJourneyAssignment: {
      method: "DELETE",
      path: "/journey-assignments/:assignmentId",
      responses: {
        200: c.type<
          NonNullable<Awaited<ReturnType<typeof deleteJourneyAssignment>>>
        >(),
        404: c.type<{ message: string }>(),
      },
      body: c.noBody(),
    },

    // Get user's assigned journeys with progress (user-facing)
    getUserJourneys: {
      method: "GET",
      path: "/users/:userId/journeys",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getUserJourneys>>>(),
      },
    },

    // Get user's progress on a specific journey
    getJourneyProgress: {
      method: "GET",
      path: "/users/:userId/journeys/:journeyId/progress",
      responses: {
        200: c.type<
          NonNullable<Awaited<ReturnType<typeof getJourneyProgress>>>
        >(),
        404: c.type<{ message: string }>(),
      },
    },

    // ========================================================================
    // Journey Notifications
    // ========================================================================

    // Admin: send manual nudge to selected assignments
    sendJourneyNudge: {
      method: "POST",
      path: "/journeys/:journeyId/nudge",
      responses: {
        200: c.type<Awaited<ReturnType<typeof sendJourneyManualNudges>>>(),
      },
      body: c.type<{
        assignmentIds: string[]
        // Dry-run: return the rendered email/Slack content + who'd be
        // sent/skipped without sending. Powers the confirmation-modal preview.
        preview?: boolean
      }>(),
    },

    // Get notification config for a journey
    getJourneyNotificationConfig: {
      method: "GET",
      path: "/journeys/:journeyId/notification-config",
      responses: {
        200: c.type<{
          config: Awaited<ReturnType<typeof getJourneyNotificationConfig>>
        }>(),
      },
    },

    // Update notification config for a journey
    updateJourneyNotificationConfig: {
      method: "PUT",
      path: "/journeys/:journeyId/notification-config",
      responses: {
        200: c.type<{
          config: Awaited<ReturnType<typeof upsertJourneyNotificationConfig>>
        }>(),
      },
      body: c.type<{
        assignmentEmailEnabled?: boolean
        dueDateRemindersEnabled?: boolean
        assignmentSlackEnabled?: boolean
        dueDateRemindersSlackEnabled?: boolean
        reminderScheduleDays?: number[]
      }>(),
    },

    // Cron: process journey due-date reminders
    processJourneyReminders: {
      method: "GET",
      path: "/scripts/send-journey-reminders",
      responses: {
        200: c.type<Awaited<ReturnType<typeof processJourneyReminders>>>(),
      },
    },
  },
  {
    baseHeaders,
  },
)

// Re-export of the Analytics Engine output names so the frontend can type the
// AnalyticsVisual `name` prop without depending on the api lib.
export type { AnalyticsOutputName } from "./lib/analytics"
